import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, useMemo } from 'react';
import { useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import type { FlowNodeData, FlowEdgeData, FlowViewport, FlowConnection, FlowOrientation } from './flow.types.js';
import { FlowNode, type PortAnchor } from './FlowNode.js';
import { FlowEdge, FlowEdgeMarkers, sourcePoint, edgePath } from './FlowEdge.js';
import './FlowCanvas.css';

/** A point in both spaces, for a consumer that opens a menu where something happened. */
export interface CanvasPoint {
  /** Flow-space (pre-zoom) coordinates: where to put a node. */
  flow: { x: number; y: number };
  /** Client pixels: where to put a popover. */
  client: { x: number; y: number };
}

export interface FlowCanvasProps {
  nodes: FlowNodeData[];
  edges: FlowEdgeData[];
  /** Called while a node drags (every move) and after an arrow-key nudge. */
  onNodesChange?: (next: FlowNodeData[]) => void;
  /** Called once when a node drag ends, after the last `onNodesChange`: the moment to record history. */
  onNodeDragEnd?: (id: string) => void;
  onEdgesChange?: (next: FlowEdgeData[]) => void;
  /** Custom renderer keyed on `node.type`. */
  nodeTypes?: Record<string, (props: { node: FlowNodeData; selected: boolean }) => ReactNode>;
  /** Selected node id (controlled). */
  selectedNodeId?: string | null;
  defaultSelectedNodeId?: string | null;
  onSelectedNodeChange?: (id: string | null) => void;
  /** Selected edge id (controlled). */
  selectedEdgeId?: string | null;
  defaultSelectedEdgeId?: string | null;
  onSelectedEdgeChange?: (id: string | null) => void;
  /** Initial viewport. */
  defaultViewport?: FlowViewport;
  viewport?: FlowViewport;
  onViewportChange?: (v: FlowViewport) => void;
  /** Min/max zoom (default 0.25 / 2). */
  minZoom?: number;
  maxZoom?: number;
  /** Show the dotted background grid. Default true. */
  withGrid?: boolean;
  className?: string;
  children?: ReactNode;
  /**
   * Enables port-to-node connections: drag from a node's out-handle and drop
   * on another node, or click the handle, then click the target. The canvas
   * only reports the intent; the consumer decides how edges change (add,
   * replace, reject cycles, …).
   */
  onConnect?: (connection: FlowConnection) => void;
  /**
   * A connection drag that ends on empty canvas. The consumer may add a node
   * at `point.flow` and link it from `source[sourceHandle]`.
   */
  onConnectEnd?: (source: string, sourceHandle: string | undefined, point: CanvasPoint) => void;
  /** Override the default (`onConnect != null`), e.g. a read-only canvas. */
  connectable?: boolean;
  /** `Delete` / `Backspace` on the focused canvas removes the selected node … */
  onDeleteNode?: (id: string) => void;
  /** … or the selected edge (also offered as a button on the selected edge). */
  onDeleteEdge?: (id: string) => void;
  /** With this, every link carries a "+" that offers to put a step on it. */
  onInsertEdge?: (id: string, anchor: { clientX: number; clientY: number }) => void;
  /** A double-click on empty canvas: the consumer may add a node there. */
  onCanvasDoubleClick?: (point: CanvasPoint) => void;
  /** The "+" beside each output port: the consumer adds the next node and links it. */
  onAddFrom?: (source: string, sourceHandle: string | undefined, anchor: PortAnchor) => void;
  /** Accessible name of the canvas region. */
  'aria-label'?: string;
  /** Reading direction: handles on the right (default) or on the bottom. */
  orientation?: FlowOrientation;
  /** Show the fit-to-view control beside the zoom controls. Default true. */
  withFitView?: boolean;
  /** Show the minimap in the bottom-left corner. Default true. */
  withMinimap?: boolean;
  /** Pan and zoom so every node is on screen the first time nodes arrive. Default false. */
  fitViewOnInit?: boolean;
  /**
   * Screen space that overlays cover (a toolbar along the top, a panel down
   * one side), in pixels. Fit-to-view keeps the nodes out of it.
   */
  fitInsets?: { top?: number; right?: number; bottom?: number; left?: number };
  /** Snap a dragged node to this grid (flow-space px) when the drag ends. 0 disables. Default 8. */
  snap?: number;
  /** Accessible names for the on-canvas controls. */
  labels?: Partial<{
    zoomIn: string; zoomOut: string; reset: string; fit: string; minimap: string; addFrom: string; removeEdge: string; insertOn: string;
  }>;
}

interface PendingConnection {
  source: string;
  /** Output port the drag began on, when the node has several. */
  sourceHandle?: string;
  /** Rubber-band end in flow-space. */
  x: number;
  y: number;
  /** Node currently under the pointer (never the source). */
  over: string | null;
  /** Where the handle press started (client px): a press without movement arms click-to-connect. */
  startClientX: number;
  startClientY: number;
  /** True after a click on the handle: the next click on a node completes the connection. */
  armed: boolean;
}

const CLICK_SLOP_PX = 4;
const MINIMAP_W = 168;
const MINIMAP_H = 104;

/**
 * Pan + zoom node canvas. Built on a CSS transform on the inner layer so
 * the maths stays simple: no extra peer (besides dnd-kit for drag-onto-
 * canvas from a palette, which is wired separately via `<DndProvider>` +
 * `<NodePalette>`).
 *
 *   <DndProvider onDragEnd={onDropFromPalette}>
 *     <NodePalette nodeTypes={types} />
 *     <FlowCanvas
 *       nodes={nodes}
 *       edges={edges}
 *       onNodesChange={setNodes}
 *       onNodeDragEnd={commitHistory}
 *       onConnect={({ source, target, sourceHandle }) => link(source, target, sourceHandle)}
 *       onDeleteNode={removeNode}
 *       nodeTypes={nodeTypes}
 *     />
 *     <NodeInspector node={selectedNode} onChange={updateNode} />
 *   </DndProvider>
 */
export function FlowCanvas(props: FlowCanvasProps): ReactNode {
  const {
    nodes, edges,
    onNodesChange, onNodeDragEnd, onEdgesChange,
    nodeTypes = {},
    selectedNodeId, defaultSelectedNodeId, onSelectedNodeChange,
    selectedEdgeId, defaultSelectedEdgeId, onSelectedEdgeChange,
    defaultViewport, viewport, onViewportChange,
    minZoom = 0.25, maxZoom = 2,
    withGrid = true, className, children,
    onConnect, onConnectEnd, connectable: connectableProp,
    onDeleteNode, onDeleteEdge, onInsertEdge, onCanvasDoubleClick, onAddFrom, fitInsets,
    'aria-label': ariaLabel,
    orientation = 'horizontal',
    withFitView = true,
    withMinimap = true,
    fitViewOnInit = false,
    snap = 8,
    labels = {}
  } = props;
  void onEdgesChange; // reserved for consumers that mutate edges through the canvas

  const [vp, setVp] = useUncontrolled<FlowViewport>({
    value: viewport,
    defaultValue: defaultViewport,
    finalValue: { x: 0, y: 0, zoom: 1 },
    onChange: onViewportChange
  });
  const [selNode, setSelNode] = useUncontrolled<string | null>({
    value: selectedNodeId,
    defaultValue: defaultSelectedNodeId,
    finalValue: null,
    onChange: onSelectedNodeChange
  });
  const [selEdge, setSelEdge] = useUncontrolled<string | null>({
    value: selectedEdgeId,
    defaultValue: defaultSelectedEdgeId,
    finalValue: null,
    onChange: onSelectedEdgeChange
  });

  const canvasRef = useRef<HTMLDivElement | null>(null);
  const panStartRef = useRef<{ x: number; y: number; vpX: number; vpY: number; moved: boolean } | null>(null);
  const connectable = connectableProp ?? Boolean(onConnect);
  const vpRef = useRef(vp);
  vpRef.current = vp;
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  const toFlowSpace = useCallback((clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const v = vpRef.current;
    if (!rect) return { x: 0, y: 0 };
    return { x: (clientX - rect.left - v.x) / v.zoom, y: (clientY - rect.top - v.y) / v.zoom };
  }, []);

  // ── Viewport helpers ─────────────────────────────────────────────────
  const clampZoom = useCallback((z: number) => Math.max(minZoom, Math.min(maxZoom, z)), [minZoom, maxZoom]);

  /** Zoom by `factor` keeping the point at client (px, py) stationary. */
  const zoomAt = useCallback((factor: number, px?: number, py?: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const v = vpRef.current;
    const nextZoom = clampZoom(v.zoom * factor);
    if (nextZoom === v.zoom || !rect) return;
    const cx = px ?? rect.width / 2;
    const cy = py ?? rect.height / 2;
    setVp({
      x: cx - ((cx - v.x) * nextZoom) / v.zoom,
      y: cy - ((cy - v.y) * nextZoom) / v.zoom,
      zoom: nextZoom
    });
  }, [clampZoom, setVp]);

  /** Which output handles already carry a link, per node: those show no label and no "+" of their own. */
  const linkedByNode = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const e of edges) {
      const set = m.get(e.source) ?? new Set<string>();
      set.add(e.sourceHandle ?? '');
      m.set(e.source, set);
    }
    return m;
  }, [edges]);

  /** Pan and zoom so every node is on screen, clear of the overlays. */
  const insetTop = fitInsets?.top ?? 0;
  const insetRight = fitInsets?.right ?? 0;
  const insetBottom = fitInsets?.bottom ?? 0;
  const insetLeft = fitInsets?.left ?? 0;
  const fitView = useCallback(() => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const list = nodesRef.current;
    if (!rect || list.length === 0) return;
    const minX = Math.min(...list.map((n) => n.position.x));
    const minY = Math.min(...list.map((n) => n.position.y));
    const maxX = Math.max(...list.map((n) => n.position.x + (n.width ?? 200)));
    const maxY = Math.max(...list.map((n) => n.position.y + (n.height ?? 80)));
    const pad = 40;
    const availW = Math.max(1, rect.width - insetLeft - insetRight - pad * 2);
    const availH = Math.max(1, rect.height - insetTop - insetBottom - pad * 2);
    const zoom = clampZoom(Math.min(
      availW / Math.max(1, maxX - minX),
      availH / Math.max(1, maxY - minY),
      1
    ));
    setVp({
      x: insetLeft + pad + (availW - (maxX - minX) * zoom) / 2 - minX * zoom,
      y: insetTop + pad + Math.max(0, (availH - (maxY - minY) * zoom) / 2) - minY * zoom,
      zoom
    });
  }, [clampZoom, setVp, insetTop, insetRight, insetBottom, insetLeft]);

  const fitted = useRef(false);
  useLayoutEffect(() => {
    if (!fitViewOnInit || fitted.current || nodes.length === 0) return;
    fitted.current = true;
    fitView();
  }, [fitViewOnInit, nodes.length, fitView]);

  // Plain wheel pans, ctrl/cmd + wheel (and a trackpad pinch, which arrives
  // as ctrl + wheel) zooms at the cursor. Attached natively and non-passive
  // so the page behind the canvas does not scroll as well.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) {
        zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX - rect.left, e.clientY - rect.top);
        return;
      }
      const v = vpRef.current;
      const scale = e.deltaMode === 1 ? 16 : 1;
      setVp({ x: v.x - e.deltaX * scale, y: v.y - e.deltaY * scale, zoom: v.zoom });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt, setVp]);

  // ── Connections (handle drag / click-to-connect) ──────────────────────
  const [pending, setPending] = useState<PendingConnection | null>(null);
  const pendingRef = useRef<PendingConnection | null>(null);
  pendingRef.current = pending;
  const onConnectRef = useRef(onConnect);
  onConnectRef.current = onConnect;
  const onConnectEndRef = useRef(onConnectEnd);
  onConnectEndRef.current = onConnectEnd;

  const startConnection = useCallback((id: string, e: React.PointerEvent<HTMLElement>, handleId?: string) => {
    const p = toFlowSpace(e.clientX, e.clientY);
    setPending({ source: id, sourceHandle: handleId, x: p.x, y: p.y, over: null, startClientX: e.clientX, startClientY: e.clientY, armed: false });
  }, [toFlowSpace]);

  useEffect(() => {
    if (!pending) return;
    const nodeIdAt = (clientX: number, clientY: number): string | null => {
      if (typeof document === 'undefined') return null;
      const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      const host = el?.closest<HTMLElement>('[data-node-id]');
      return host?.dataset.nodeId ?? null;
    };
    const onMove = (e: PointerEvent) => {
      const p = toFlowSpace(e.clientX, e.clientY);
      const over = nodeIdAt(e.clientX, e.clientY);
      setPending((cur) => (cur ? { ...cur, x: p.x, y: p.y, over: over && over !== cur.source ? over : null } : cur));
    };
    const onUp = (e: PointerEvent) => {
      const cur = pendingRef.current;
      if (!cur) return;
      const target = nodeIdAt(e.clientX, e.clientY);
      const moved = Math.hypot(e.clientX - cur.startClientX, e.clientY - cur.startClientY) > CLICK_SLOP_PX;
      if (!cur.armed && !moved && (target === null || target === cur.source)) {
        // A plain click on the handle: arm click-to-connect and keep the rubber band.
        setPending({ ...cur, armed: true });
        return;
      }
      setPending(null);
      if (target && target !== cur.source) {
        onConnectRef.current?.({ source: cur.source, target, sourceHandle: cur.sourceHandle });
      } else if (moved && target === null) {
        // Let go on empty canvas: the consumer may put a new node there.
        onConnectEndRef.current?.(cur.source, cur.sourceHandle, {
          flow: toFlowSpace(e.clientX, e.clientY),
          client: { x: e.clientX, y: e.clientY }
        });
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // Subscribe once per connection session; positions flow through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending?.source, toFlowSpace]);

  // ── Node drag ─────────────────────────────────────────────────────────
  const moveNode = useCallback((id: string, delta: { x: number; y: number }, snapTo = 0) => {
    if (!onNodesChange) return;
    const next = nodesRef.current.map((n) => {
      if (n.id !== id) return n;
      let x = n.position.x + delta.x / vpRef.current.zoom;
      let y = n.position.y + delta.y / vpRef.current.zoom;
      if (snapTo > 0) { x = Math.round(x / snapTo) * snapTo; y = Math.round(y / snapTo) * snapTo; }
      return { ...n, position: { x, y } };
    });
    onNodesChange(next);
  }, [onNodesChange]);

  const endNodeDrag = useCallback((id: string) => {
    if (snap > 0) moveNode(id, { x: 0, y: 0 }, snap);
    onNodeDragEnd?.(id);
  }, [moveNode, onNodeDragEnd, snap]);

  // ── Canvas pointer: pan, clear selection, double-click ────────────────
  const onCanvasPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.button !== 1) return;
    const onBackground = (e.target as HTMLElement).closest('[data-node-id], .rr-flow-edge, .rr-flow-canvas__controls, .rr-flow-canvas__minimap') === null;
    if (!onBackground) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    panStartRef.current = { x: e.clientX, y: e.clientY, vpX: vp.x, vpY: vp.y, moved: false };
  };
  const onCanvasPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = panStartRef.current;
    if (!p) return;
    if (!p.moved && Math.hypot(e.clientX - p.x, e.clientY - p.y) < CLICK_SLOP_PX) return;
    p.moved = true;
    setVp({ x: p.vpX + (e.clientX - p.x), y: p.vpY + (e.clientY - p.y), zoom: vp.zoom });
  };
  const onCanvasPointerUp = () => {
    const p = panStartRef.current;
    panStartRef.current = null;
    if (p && !p.moved) {
      // A click on the background clears the selection (and an armed connection).
      setSelNode(null);
      setSelEdge(null);
      if (pendingRef.current?.armed) setPending(null);
    }
  };
  const onDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const onBackground = (e.target as HTMLElement).closest('[data-node-id], .rr-flow-edge, .rr-flow-canvas__controls, .rr-flow-canvas__minimap') === null;
    if (!onBackground || !onCanvasDoubleClick) return;
    onCanvasDoubleClick({ flow: toFlowSpace(e.clientX, e.clientY), client: { x: e.clientX, y: e.clientY } });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement;
    const inEditable = t !== canvasRef.current
      && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
    if (inEditable) return;
    if (e.key === 'Escape') {
      setPending(null);
      setSelNode(null);
      setSelEdge(null);
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (selNode && onDeleteNode) {
        e.preventDefault();
        onDeleteNode(selNode);
        setSelNode(null);
      } else if (selEdge && onDeleteEdge) {
        e.preventDefault();
        onDeleteEdge(selEdge);
        setSelEdge(null);
      }
      return;
    }
    // Arrow keys nudge the selected node; Shift for a bigger step.
    if (selNode && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      const step = (e.shiftKey ? 32 : 8) * vp.zoom;
      const delta = e.key === 'ArrowUp' ? { x: 0, y: -step } : e.key === 'ArrowDown' ? { x: 0, y: step } : e.key === 'ArrowLeft' ? { x: -step, y: 0 } : { x: step, y: 0 };
      moveNode(selNode, delta);
      onNodeDragEnd?.(selNode);
    }
  };

  const transform = `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})`;

  const pendingSource = pending ? nodes.find((n) => n.id === pending.source) : undefined;
  let pendingPath: string | null = null;
  if (pending && pendingSource) {
    pendingPath = edgePath(sourcePoint(pendingSource, pending.sourceHandle, orientation), { x: pending.x, y: pending.y }, orientation);
  }

  // ── Minimap ───────────────────────────────────────────────────────────
  const rect = canvasRef.current?.getBoundingClientRect();
  const bounds = nodes.length
    ? {
        minX: Math.min(...nodes.map((n) => n.position.x)) - 40,
        minY: Math.min(...nodes.map((n) => n.position.y)) - 40,
        maxX: Math.max(...nodes.map((n) => n.position.x + (n.width ?? 200))) + 40,
        maxY: Math.max(...nodes.map((n) => n.position.y + (n.height ?? 80))) + 40
      }
    : null;
  const miniScale = bounds ? Math.min(MINIMAP_W / (bounds.maxX - bounds.minX), MINIMAP_H / (bounds.maxY - bounds.minY)) : 1;
  const miniView = bounds && rect
    ? {
        x: (-vp.x / vp.zoom - bounds.minX) * miniScale,
        y: (-vp.y / vp.zoom - bounds.minY) * miniScale,
        w: (rect.width / vp.zoom) * miniScale,
        h: (rect.height / vp.zoom) * miniScale
      }
    : null;
  const onMinimapClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!bounds || !rect) return;
    const box = e.currentTarget.getBoundingClientRect();
    const fx = bounds.minX + (e.clientX - box.left) / miniScale;
    const fy = bounds.minY + (e.clientY - box.top) / miniScale;
    setVp({ x: rect.width / 2 - fx * vp.zoom, y: rect.height / 2 - fy * vp.zoom, zoom: vp.zoom });
  };

  const connectedToSelection = new Set<string>();
  if (selNode) for (const e of edges) if (e.source === selNode || e.target === selNode) connectedToSelection.add(e.id);

  return (
    <div
      ref={canvasRef}
      className={['rr-flow-canvas', withGrid && 'rr-flow-canvas--grid', orientation === 'vertical' && 'rr-flow-canvas--vertical', className].filter(Boolean).join(' ')}
      onPointerDown={onCanvasPointerDown}
      onPointerMove={onCanvasPointerMove}
      onPointerUp={onCanvasPointerUp}
      onPointerCancel={() => { panStartRef.current = null; }}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
      tabIndex={0}
      aria-label={ariaLabel ?? 'Flow canvas'}
      data-zoom={vp.zoom.toFixed(2)}
      data-connecting={pending ? '' : undefined}
    >
      <div className="rr-flow-canvas__layer" style={{ transform, transformOrigin: '0 0' }}>
        {/* The edge layer is a 0×0 SVG at the layer's origin with visible
            overflow, so flow-space coordinates draw where the nodes are. */}
        <svg className="rr-flow-canvas__edges" width={1} height={1}>
          <FlowEdgeMarkers />
          {edges.map((e) => {
            const s = nodes.find((n) => n.id === e.source);
            const t = nodes.find((n) => n.id === e.target);
            if (!s || !t) return null;
            return (
              <FlowEdge
                key={e.id}
                edge={e}
                source={s}
                target={t}
                selected={selEdge === e.id}
                muted={Boolean(selNode) && !connectedToSelection.has(e.id)}
                orientation={orientation}
                onClick={(edge) => { setSelEdge(edge.id); setSelNode(null); }}
                onDelete={onDeleteEdge ? (edge) => { onDeleteEdge(edge.id); setSelEdge(null); } : undefined}
                deleteLabel={labels.removeEdge}
                onInsert={onInsertEdge ? (edge, anchor) => onInsertEdge(edge.id, anchor) : undefined}
                insertLabel={labels.insertOn}
              />
            );
          })}
          {pendingPath ? (
            <path
              className="rr-flow-edge--pending"
              d={pendingPath}
              stroke="var(--rr-color-primary)"
              strokeWidth={2}
              strokeDasharray="6 5"
              fill="none"
              pointerEvents="none"
              markerEnd="url(#rr-flow-arrow-active)"
            />
          ) : null}
        </svg>

        {nodes.map((n) => {
          const Custom = nodeTypes[n.type];
          return (
            <FlowNode
              key={n.id}
              node={n}
              linkedHandles={linkedByNode.get(n.id)}
              selected={selNode === n.id}
              onClick={(id) => { setSelNode(id); setSelEdge(null); }}
              onDrag={(id, delta) => moveNode(id, delta)}
              onDragEnd={endNodeDrag}
              connectable={connectable}
              onConnectStart={startConnection}
              connectTarget={pending?.over === n.id}
              orientation={orientation}
              onAddFrom={onAddFrom}
              addLabel={labels.addFrom}
            >
              {Custom ? <Custom node={n} selected={selNode === n.id} /> : null}
            </FlowNode>
          );
        })}

        {children}
      </div>

      <div className="rr-flow-canvas__controls">
        <button type="button" onClick={() => zoomAt(1.2)} aria-label={labels.zoomIn ?? 'Zoom in'}>+</button>
        <span className="rr-flow-canvas__zoom" aria-live="polite">{Math.round(vp.zoom * 100)}%</span>
        <button type="button" onClick={() => zoomAt(1 / 1.2)} aria-label={labels.zoomOut ?? 'Zoom out'}>−</button>
        <button type="button" onClick={() => setVp({ x: 0, y: 0, zoom: 1 })} aria-label={labels.reset ?? 'Reset view'}>⟳</button>
        {withFitView ? <button type="button" onClick={fitView} aria-label={labels.fit ?? 'Fit to view'}>⛶</button> : null}
      </div>

      {withMinimap && bounds && miniView ? (
        <svg
          className="rr-flow-canvas__minimap"
          width={MINIMAP_W}
          height={MINIMAP_H}
          viewBox={`0 0 ${MINIMAP_W} ${MINIMAP_H}`}
          role="img"
          aria-label={labels.minimap ?? 'Overview'}
          onClick={onMinimapClick}
        >
          {nodes.map((n) => (
            <rect
              key={n.id}
              x={(n.position.x - bounds.minX) * miniScale}
              y={(n.position.y - bounds.minY) * miniScale}
              width={Math.max(2, (n.width ?? 200) * miniScale)}
              height={Math.max(2, (n.height ?? 80) * miniScale)}
              rx={1.5}
              className={selNode === n.id ? 'rr-flow-canvas__mini-node rr-flow-canvas__mini-node--selected' : 'rr-flow-canvas__mini-node'}
            />
          ))}
          <rect className="rr-flow-canvas__mini-view" x={miniView.x} y={miniView.y} width={miniView.w} height={miniView.h} rx={2} />
        </svg>
      ) : null}
    </div>
  );
}
